import request from '#/utils/request';
import { parseVer4DetailResponse } from '#/api/adapters/envelope';
import { coPath, toWire, withIdempotencyKey } from './shared';

/** GET `…/collection-receipts` → `{ receipts[], summary }`（`fms.customer_collection.read`）。 */
export async function fetchCollectionReceipts(id) {
  const { data: raw } = await request.get(coPath(id, '/collection-receipts'));
  return parseVer4DetailResponse(raw);
}

/** GET `…/collection-receipts/summary`：只有彙總（`crm.customer_collection_summary.read`）。 */
export async function fetchCollectionSummary(id) {
  const { data: raw } = await request.get(coPath(id, '/collection-receipts/summary'));
  return parseVer4DetailResponse(raw);
}

/** POST `…/collection-receipts`：建立草稿 `{ amount, actualReceivedAt, remark }`（`.update`）。 */
export async function createCollectionReceiptDraft(id, draft) {
  const { data: raw } = await request.post(coPath(id, '/collection-receipts'), toWire(draft));
  return parseVer4DetailResponse(raw);
}

/** PATCH `…/collection-receipts/{receiptId}`：`{ expectedVersionNo, draft }`（`.update`）。 */
export async function updateCollectionReceiptDraft(id, receiptId, payload) {
  const { data: raw } = await request.patch(
    coPath(id, `/collection-receipts/${receiptId}`),
    toWire(payload),
  );
  return parseVer4DetailResponse(raw);
}

/** POST `…/{receiptId}/confirm`：`{ expectedVersionNo, credits[{ phaseId, amount }] }`（`.confirm`）。 */
export async function confirmCollectionReceipt(id, receiptId, payload, { idempotencyKey }) {
  const { data: raw } = await request.post(
    coPath(id, `/collection-receipts/${receiptId}/confirm`),
    toWire(payload),
    withIdempotencyKey(idempotencyKey),
  );
  return parseVer4DetailResponse(raw);
}

/**
 * POST `…/collection-receipts/with-attachments`（multipart）：`record` JSON part
 * `{ draft, credits, confirm }`、`files` 可重複、`attachment_remark`。
 * 上傳未全成功時草稿保留不確認，用同 key 同內容重送補齊。
 */
export async function createCollectionReceiptWithAttachments(
  id,
  { draft, credits = [], confirm = false, files = [], attachmentRemark },
  { idempotencyKey },
) {
  const form = new FormData();
  form.append(
    'record',
    new Blob([JSON.stringify(toWire({ draft, credits, confirm }))], { type: 'application/json' }),
  );
  files.forEach((file) => form.append('files', file));
  if (attachmentRemark) {
    form.append('attachment_remark', attachmentRemark);
  }
  const { data: raw } = await request.post(
    coPath(id, '/collection-receipts/with-attachments'),
    form,
    {
      headers: {
        ...withIdempotencyKey(idempotencyKey).headers,
        // 讓瀏覽器自帶 boundary
        'Content-Type': undefined,
      },
    },
  );
  return parseVer4DetailResponse(raw);
}

/**
 * POST `…/{receiptId}/void`（PAYMENT_VOID）：`amount` 須為原收款全額，反向列須涵蓋全部有效抵付與分配。
 * body 同退款：`actualOccurredAt`、`reason`、`expectedReceiptAvailable`、`phaseReversals[]`、`salesReversals[]`。
 */
export async function voidCollectionReceipt(id, receiptId, payload, { idempotencyKey }) {
  const { data: raw } = await request.post(
    coPath(id, `/collection-receipts/${receiptId}/void`),
    toWire(payload),
    withIdempotencyKey(idempotencyKey),
  );
  return parseVer4DetailResponse(raw);
}
